// Finlightenment Financial Fitness Quiz — full content from the build guide
// Verbatim copy from the Kajabi Build Guide. Do NOT paraphrase.

export const QUIZ_INTRO = {
  badge: "90-Second Financial Fitness Check",
  headline: "How Financially Fit Are You — Really?",
  subhead:
    "Take 90 seconds to discover your financial strengths, uncover your blind spots, and get a personalized plan across all 6 pillars of your financial life.",
  body: [
    "You're earning, saving, maybe even investing — but are you actually where you should be?",
    "Most people miss critical money steps without even realizing it — not because they're irresponsible, but because no one ever taught them the full picture.",
    "This quiz checks your financial fitness across 6 key pillars:",
  ],
  pillars: [
    "Mindset & Milestones",
    "Money Management",
    "Investing",
    "Insurance",
    "Legal",
    "Taxes",
  ],
  closing:
    "In 90 seconds, you'll get a clear snapshot of where you stand — and exactly where to focus next for the biggest results.",
};

export const PILLARS = {
  mindset: { key: "mindset", label: "Mindset & Milestones", short: "Mindset" },
  money: { key: "money", label: "Money Management", short: "Money Mgmt" },
  investing: { key: "investing", label: "Investing", short: "Investing" },
  insurance: { key: "insurance", label: "Insurance", short: "Insurance" },
  legal: { key: "legal", label: "Legal", short: "Legal" },
  taxes: { key: "taxes", label: "Taxes", short: "Taxes" },
};

// Each option: text (Kajabi answer copy short label), feedback (the longer Kajabi answer copy), score
export const QUESTIONS = [
  {
    id: 1,
    pillar: "mindset",
    prompt: "When you think about your financial future, how do you feel?",
    options: [
      { letter: "A", text: "Confident & excited", score: 2, feedback: "You have a positive, forward-looking relationship with money — that's the fuel for everything else." },
      { letter: "B", text: "Unsure & overwhelmed", score: 1, feedback: "That's normal — and fixable. Most people feel this way until they build a system that works." },
      { letter: "C", text: "I avoid thinking about it", score: 0, feedback: "Avoidance is your brain's way of protecting you from stress — but it costs you more in the long run. This is the #1 thing to change." },
    ],
  },
  {
    id: 2,
    pillar: "mindset",
    prompt: "Which statement feels most true about building wealth?",
    options: [
      { letter: "A", text: "It's absolutely possible for me", score: 2, feedback: "This belief is one of the strongest predictors of financial success. You're already ahead." },
      { letter: "B", text: "Maybe — if I get it together", score: 1, feedback: "That conditional mindset is common. Once you have a plan, the 'maybe' tends to become a 'definitely.'" },
      { letter: "C", text: "It happens to other people, not me", score: 0, feedback: "This is one of the most limiting money beliefs — and it's almost always wrong. Let's work on this together." },
    ],
  },
  {
    id: 3,
    pillar: "mindset",
    prompt: "Do you have a clear, written picture of what financial success looks like for YOU in the next 5–10 years?",
    options: [
      { letter: "A", text: "Yes — written and specific", score: 2, feedback: "Written goals are dramatically more likely to be achieved. You're operating with a real target." },
      { letter: "B", text: "I have a general idea", score: 1, feedback: "A general idea is a great start — but vague goals produce vague results. Let's sharpen it." },
      { letter: "C", text: "I've been focused on surviving, not planning", score: 0, feedback: "Totally understandable. Survival mode is real. But even one written goal changes your relationship with money." },
    ],
  },
  {
    id: 4,
    pillar: "money",
    prompt: "Do you know exactly where every dollar of your income goes each month?",
    options: [
      { letter: "A", text: "Yes — down to the dollar", score: 2, feedback: "Financial awareness at this level is rare and powerful. You have the clarity most people spend years chasing." },
      { letter: "B", text: "I have a rough idea", score: 1, feedback: "A rough idea is a start — but the money you can't see is the money you can't control." },
      { letter: "C", text: "No idea — it just disappears", score: 0, feedback: "You're not alone. This is the most common financial gap I see. The great news: it's the most fixable." },
    ],
  },
  {
    id: 5,
    pillar: "money",
    prompt: "Do you have a working budget that guides your spending and saving decisions every month?",
    options: [
      { letter: "A", text: "Yes — I use it every month", score: 2, feedback: "A consistent monthly budget is the single most powerful wealth-building habit you can have. Keep going." },
      { letter: "B", text: "I've tried but it never sticks", score: 1, feedback: "Most budgets fail because they only address the numbers — not the behavior behind them. The right system changes that." },
      { letter: "C", text: "No — I've never really budgeted", score: 0, feedback: "This is your biggest opportunity. A budget isn't a restriction — it's a plan for what matters to you." },
    ],
  },
  {
    id: 6,
    pillar: "money",
    prompt: "Do you have credit card debt — and a specific, written plan to pay it off?",
    options: [
      { letter: "A", text: "No debt — I pay in full monthly", score: 2, feedback: "Paying in full every month is one of the highest-value financial habits. You're building real net worth." },
      { letter: "B", text: "I have debt and a general plan", score: 1, feedback: "Having a plan is good — but a general plan tends to stay general. A specific monthly target and payoff date changes everything." },
      { letter: "C", text: "I have debt and no real plan", score: 0, feedback: "Credit card debt is one of the most expensive financial drains — and one of the most solvable. You just need the right system." },
    ],
  },
  {
    id: 7,
    pillar: "money",
    prompt: "Do you have an emergency fund — and how much do you have saved?",
    options: [
      { letter: "A", text: "Yes — 3–6+ months of expenses", score: 2, feedback: "A fully funded emergency fund is your financial immune system. It changes how every other financial decision feels." },
      { letter: "B", text: "I have something saved but not enough", score: 1, feedback: "Starting is the hardest part — and you've done it. Now let's build it to where it actually protects you." },
      { letter: "C", text: "Nothing saved — it never builds", score: 0, feedback: "Every time you save and then spend it, that's not failure — that's a system problem. Let's fix the system." },
    ],
  },
  {
    id: 8,
    pillar: "investing",
    prompt: "Are you saving at least 15% of your household income toward retirement (including any employer match)?",
    options: [
      { letter: "A", text: "Yes — 15% or more", score: 2, feedback: "You're in the top tier of retirement savers. Consistent contributions at this rate build wealth that compounds powerfully over time." },
      { letter: "B", text: "Something — but less than 15%", score: 1, feedback: "Something is always better than nothing. Even a 1% increase per year adds up significantly over a career." },
      { letter: "C", text: "Not even close", score: 0, feedback: "Retirement savings is one of the most time-sensitive moves in personal finance — every year earlier matters enormously." },
    ],
  },
  {
    id: 9,
    pillar: "investing",
    prompt: "Do you understand the difference between a Roth IRA and a Traditional IRA — and which one is right for you?",
    options: [
      { letter: "A", text: "Yes — and I'm using them strategically", score: 2, feedback: "Tax-advantaged account strategy is advanced financial literacy. You're doing better than most." },
      { letter: "B", text: "Somewhat — but I'm not sure what's right for me", score: 1, feedback: "Understanding the concept is the first step. Applying it to your specific situation is the second — and we can help with that." },
      { letter: "C", text: "Not really", score: 0, feedback: "The Roth vs. Traditional decision can be worth thousands over your lifetime. It's worth understanding — and it's simpler than it sounds." },
    ],
  },
  {
    id: 10,
    pillar: "investing",
    prompt: "Are you confident that your investments are well-diversified and aligned with your age, goals, and risk tolerance?",
    options: [
      { letter: "A", text: "Yes — reviewed recently and on track", score: 2, feedback: "Regular portfolio reviews aligned with your life stage are a hallmark of sophisticated investing. Well done." },
      { letter: "B", text: "I have some mix of investments but haven't really reviewed it", score: 1, feedback: "Set-it-and-forget-it is fine early on — but life changes mean your portfolio should change too." },
      { letter: "C", text: "I have no idea what I'm invested in", score: 0, feedback: "Not knowing your investments is like not knowing what's in your pantry — you can't cook a good meal from random ingredients." },
    ],
  },
  {
    id: 11,
    pillar: "insurance",
    prompt: "Are you covered by disability insurance — and do you know how much income it would replace if you couldn't work?",
    options: [
      { letter: "A", text: "Yes — I know my coverage and feel protected", score: 2, feedback: "Disability insurance is the most undervalued protection most people have. You're one of the few who truly know where you stand." },
      { letter: "B", text: "I think I have something through work, but I'm not sure", score: 1, feedback: "Most employer disability plans only cover 60% of your base salary — which may not be enough. It's worth taking 10 minutes to look this up." },
      { letter: "C", text: "No idea — I've never looked into it", score: 0, feedback: "Your income is your most valuable financial asset. Protecting it with disability insurance is one of the most important moves you can make." },
    ],
  },
  {
    id: 12,
    pillar: "insurance",
    prompt: "Do you know how much life insurance you have — and whether it's enough to protect your family?",
    options: [
      { letter: "A", text: "Yes — I'm confident in my coverage", score: 2, feedback: "Knowing your coverage is the first step; ensuring it's adequate is the second. If you've done both, you're well ahead of most." },
      { letter: "B", text: "I have some, but I'm not sure if it's enough", score: 1, feedback: "A common rule of thumb: 10–12x your annual income. If you're not sure whether you're there, it's worth a quick review." },
      { letter: "C", text: "I don't have any — or have no idea", score: 0, feedback: "Life insurance is the financial safety net for the people who depend on you. Even a term policy is simple and surprisingly affordable." },
    ],
  },
  {
    id: 13,
    pillar: "legal",
    prompt: "Do you have a will, durable power of attorney, and healthcare directive — all reviewed in the last 5 years?",
    options: [
      { letter: "A", text: "Yes — all three, up to date", score: 2, feedback: "Having all three legal documents current is one of the highest acts of care for the people you love. Genuinely well done." },
      { letter: "B", text: "I have some, but not all", score: 1, feedback: "Having some is better than none — but each document serves a unique protective function. Let's close the gaps." },
      { letter: "C", text: "None — I haven't done this yet", score: 0, feedback: "Without these documents, the courts (not you) decide what happens to your assets and your healthcare. This is urgent." },
    ],
  },
  {
    id: 14,
    pillar: "legal",
    prompt: "Have you reviewed beneficiary designations on all your accounts in the last 3 years — or after any major life change?",
    options: [
      { letter: "A", text: "Yes — all reviewed and current", score: 2, feedback: "Beneficiary designations override everything in your will. Keeping them current is one of the smartest legal moves you can make." },
      { letter: "B", text: "Not recently — it's been a while", score: 1, feedback: "Life changes constantly — but beneficiary forms often don't get updated. A 30-minute review can prevent years of family conflict." },
      { letter: "C", text: "I don't really know what a beneficiary is", score: 0, feedback: "A beneficiary is the person who receives your assets when you pass — on your retirement accounts, life insurance, and more. This is critical to understand." },
    ],
  },
  {
    id: 15,
    pillar: "taxes",
    prompt: "Do you feel confident you're using all available tax-advantaged accounts (IRA, 401k, HSA, 529, FSA) to maximize your after-tax wealth?",
    options: [
      { letter: "A", text: "Yes — I'm optimizing my tax strategy", score: 2, feedback: "Proactive tax strategy can add hundreds of thousands to your lifetime wealth. You're playing the long game correctly." },
      { letter: "B", text: "I have some accounts but I'm not sure I'm maximizing them", score: 1, feedback: "Most people have the accounts — they just don't know the rules well enough to fully leverage them. That's a fixable gap." },
      { letter: "C", text: "I'm not sure what those accounts are", score: 0, feedback: "Tax-advantaged accounts are the government's way of legally letting you keep more of your money. This tutorial could be worth thousands." },
    ],
  },
];

export const TIERS = [
  {
    id: 1,
    name: "The Awakened Beginner",
    range: "0–10 points",
    min: 0,
    max: 10,
    tagline: "You're not behind. You're just early. And that changes everything.",
    body: "The fact that you took this quiz tells me something important about you: you want things to be different. That desire is the only real starting point. You don't need to fix everything at once — you need a foundation. The Financial Foundation Reset was built specifically for this moment. In 21 days, you'll have the mindset tools, the budgeting system, and the clarity to finally feel in control of your money.",
    strengths: [
      "You showed up — and that's rarer than you think",
      "You're open to learning, which means you're open to changing",
      "Your financial future is 100% still in front of you",
    ],
    focus: [
      "Build your money mindset foundation — understanding WHY you behave the way you do with money",
      "Create your first working budget with a system designed to actually stick",
      "Start (or rebuild) your emergency fund with a simple, repeatable plan",
      "Learn the basics of credit, debt, and how to protect your income and family",
    ],
    nextStepKicker: "Your Next Step",
    nextStepHeadline:
      "Join the Financial Foundation Reset — your 21-day guided program to build the money mindset and budgeting system that changes everything.",
    nextStepSub:
      "Founding Member pricing: $147. Limited spots available. Click below to learn more and reserve your seat.",
  },
  {
    id: 2,
    name: "The Momentum Builder",
    range: "11–20 points",
    min: 11,
    max: 20,
    tagline: "You've done real things. Now let's make them work together.",
    body: "You're not starting from zero — you've built some solid habits, made some smart moves, and you're clearly paying attention. But there are likely gaps: areas where you know you should be doing something but haven't quite gotten there. Or areas where you have the pieces but they're not working as a system yet. The Financial Foundation Reset gives you a clean, integrated system where every habit reinforces the next.",
    strengths: [
      "You have real financial awareness across multiple areas",
      "You've already taken action — now you're ready to optimize",
      "You understand money well enough to ask the right questions",
    ],
    focus: [
      "Tighten your budget and connect it to specific goals that light you up",
      "Review and upgrade your insurance coverage — especially disability",
      "Ensure your legal documents are complete, current, and clearly communicated",
      "Optimize your tax-advantaged accounts to stop leaving money on the table",
    ],
    nextStepKicker: "Your Next Step",
    nextStepHeadline:
      "Start with the Financial Foundation Reset — and we'll guide your next move from there.",
    nextStepSub:
      "More tools across all 6 pillars are coming soon — including the full Financial Freedom Academy. For now, the Reset is the most direct path to closing the gaps you already see.",
  },
  {
    id: 3,
    name: "The Financial Pro-In-Progress",
    range: "21–30 points",
    min: 21,
    max: 30,
    tagline: "You've built something real. Now let's make sure nothing is slipping through the cracks.",
    body: "You're doing more than most people ever will with their money — and it shows. You have systems, awareness, and confidence. But even the most financially savvy people have blind spots: a beneficiary that hasn't been updated, a tax strategy that could be sharper, a portfolio allocation that made sense five years ago but needs a refresh. Stay close — the deeper Academy modules are coming, and you'll be the first to hear when they open.",
    strengths: [
      "You have strong financial systems and habits across multiple pillars",
      "You're proactive — not reactive — with your money",
      "You understand the big picture and how the pieces connect",
    ],
    focus: [
      "Audit your investment fees and tax efficiency — even small leaks compound to big costs",
      "Review and stress-test your estate plan: are your documents, beneficiaries, and legacy plan truly current?",
      "Examine your behavioral biases — even seasoned investors can be their own biggest risk",
      "Explore advanced strategies: backdoor Roth, HSA optimization, tax-loss harvesting, etc.",
    ],
    nextStepKicker: "Your Next Step",
    nextStepHeadline:
      "Start with the Financial Foundation Reset — and stay tuned for the deeper Academy modules.",
    nextStepSub:
      "Tier 2 & Tier 3 advanced modules of the Financial Freedom Academy are coming soon. For now, the Reset is the entry point — and the only program currently open. We'll email you the moment the Academy opens.",
  },
];

export const PILLAR_FEEDBACK = {
  mindset: {
    0: "Money avoidance is costing you daily. Start with the nervous system + identity module before anything else.",
    1: "You have some awareness but limiting beliefs are likely slowing your progress. Mindset module is your priority.",
    2: "Strong money mindset. Your beliefs are working for you — now pair them with equally strong systems.",
  },
  money: {
    0: "You're flying blind financially. The budget + emergency fund foundation is your #1 next move.",
    1: "You have some systems but they're not airtight. Tighten your budget and build a real debt payoff plan.",
    2: "Your money management is solid. Focus on optimization: cash flow efficiency, savings rate, and goal alignment.",
  },
  investing: {
    0: "Retirement savings needs immediate attention — time is your most valuable investing asset. Start now.",
    1: "You're saving something — now let's maximize it. Roth vs Traditional strategy could be worth thousands.",
    2: "Strong investing foundation. Look at fees, tax efficiency, and whether your allocation still fits your timeline.",
  },
  insurance: {
    0: "Your income and family are exposed. Disability and life insurance should be reviewed this week.",
    1: "You have some coverage — but coverage gaps are dangerous. Get a clear picture of what you actually have.",
    2: "Well-protected. Review your coverage annually and after any major life change.",
  },
  legal: {
    0: "No estate documents = courts decide everything for you. This is urgent regardless of your net worth.",
    1: "Partial documents are better than none — but incomplete plans create real risk. Close the gaps now.",
    2: "Strong legal foundation. Keep documents current and review beneficiaries every 3 years.",
  },
  taxes: {
    0: "You're likely leaving significant money on the table. Tax-advantaged accounts are one of the highest-ROI financial moves.",
    1: "You have the accounts — now learn to maximize them. HSA, Roth, and 529 strategy can save thousands.",
    2: "Proactive tax strategy is a wealth multiplier. Look at advanced strategies: backdoor Roth, tax-loss harvesting.",
  },
};

// Per-pillar avg threshold helper: average score per pillar question
export function pillarBucket(avg) {
  if (avg < 0.67) return 0;
  if (avg < 1.34) return 1;
  return 2;
}

export function computeQuizResult(answers) {
  // answers: { [questionId]: 'A'|'B'|'C' }
  let total = 0;
  const pillarTotals = {};
  const pillarCounts = {};

  for (const q of QUESTIONS) {
    const letter = answers[q.id];
    const opt = q.options.find((o) => o.letter === letter);
    const s = opt ? opt.score : 0;
    total += s;
    pillarTotals[q.pillar] = (pillarTotals[q.pillar] || 0) + s;
    pillarCounts[q.pillar] = (pillarCounts[q.pillar] || 0) + 1;
  }

  const tier = TIERS.find((t) => total >= t.min && total <= t.max) || TIERS[0];

  const pillarBreakdown = Object.keys(PILLARS).map((key) => {
    const earned = pillarTotals[key] || 0;
    const count = pillarCounts[key] || 0;
    const max = count * 2;
    const avg = count > 0 ? earned / count : 0;
    const bucket = pillarBucket(avg);
    return {
      key,
      label: PILLARS[key].label,
      short: PILLARS[key].short,
      earned,
      max,
      pct: max > 0 ? Math.round((earned / max) * 100) : 0,
      bucket,
      feedback: PILLAR_FEEDBACK[key][bucket],
    };
  });

  // Lowest scoring pillar (for "focus area")
  const sortedAsc = [...pillarBreakdown].sort((a, b) => a.pct - b.pct);
  const focusPillar = sortedAsc[0];
  const strongestPillar = sortedAsc[sortedAsc.length - 1];

  return { total, max: 30, tier, pillarBreakdown, focusPillar, strongestPillar };
}
